/**
 * Portal home — Step 71, shaped like the reference's portal_home: a branded
 * hero greeting, quick-action cards, and the person's open requests with a
 * View-all. (The reference's Freddy search box and announcements strip are
 * later steps, not silently faked.)
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, ClipboardList, LayoutGrid, Plus, TriangleAlert } from 'lucide-react';
import { portalApi } from '../../features/portal/api';
import { StatusChip } from '../../features/portal/StatusChip';
import { Skeleton } from '../../components/ui/skeleton';
import { relativeTime } from '../../lib/time';
import { qk } from '../../lib/queryClient';
import { useAuth } from '../../lib/AuthContext';
import { PortalSearch } from '../../features/portal/PortalSearch';
import { ticketRef } from '@itsm/shared';

export function PortalHomePage() {
  const { me } = useAuth();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.portalTickets,
    queryFn: portalApi.myTickets,
  });
  const open = (data?.entries ?? []).filter(
    (t) => t.statusCategory === 'open' || t.statusCategory === 'pending',
  );
  const firstName = (me?.user.name ?? '').split(' ')[0] || null;

  return (
    <div className="space-y-6">
      <section className="rounded-xl bg-brand px-8 py-10 text-center text-white">
        <h1 className="text-2xl font-semibold">Hi{firstName ? ` ${firstName}` : ''}, how can we help you?</h1>
        <p className="mt-1 text-sm text-white/80">
          Search your requests and our services, or raise something new.
        </p>
        {/* The reference's hero search — the big box in the middle (Kai),
            not a strip squeezed into the header. */}
        <div className="mx-auto mt-5 flex justify-center">
          <PortalSearch large />
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Link
          to="/portal/catalogue"
          className="group rounded-lg border bg-card p-5 transition-colors hover:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <LayoutGrid className="mb-2 h-6 w-6 text-brand" />
          <p className="text-sm font-semibold group-hover:text-brand">Request a service</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Browse the list of services offered and raise a request.
          </p>
        </Link>
        <Link
          to="/portal/report"
          className="group rounded-lg border bg-card p-5 transition-colors hover:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <TriangleAlert className="mb-2 h-6 w-6 text-amber-600 dark:text-amber-400" />
          <p className="text-sm font-semibold group-hover:text-brand">Report an issue</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Having trouble? Tell the support team what isn't working.
          </p>
        </Link>
        <Link
          to="/portal/help"
          className="group rounded-lg border bg-card p-5 transition-colors hover:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <BookOpen className="mb-2 h-6 w-6 text-brand" />
          <p className="text-sm font-semibold group-hover:text-brand">Browse help articles</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Look up guides and how-tos to fix things on your own.
          </p>
        </Link>
        <Link
          to="/portal/requests"
          className="group rounded-lg border bg-card p-5 transition-colors hover:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ClipboardList className="mb-2 h-6 w-6 text-brand" />
          <p className="text-sm font-semibold group-hover:text-brand">My requests</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Check the status of everything you've raised.
          </p>
        </Link>
      </section>

      <section>
        <div className="mb-2 flex items-center">
          <h2 className="text-sm font-semibold">Open requests</h2>
          <Link to="/portal/requests" className="ml-auto text-xs text-brand hover:underline">
            View all
          </Link>
        </div>
        {isError && (
          <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
            <p className="text-sm font-medium text-destructive">Could not load your requests</p>
            <p className="text-xs text-destructive/80">{(error as Error).message}</p>
            <button
              onClick={() => refetch()}
              className="text-xs text-destructive/80 underline hover:text-destructive"
            >
              Retry
            </button>
          </div>
        )}
        {isPending && (
          <div className="space-y-2">
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        )}
        {data && open.length === 0 && (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            Nothing open right now.{' '}
            <Link to="/portal/catalogue" className="text-brand hover:underline">
              Raise a request
            </Link>
            <Plus className="ml-0.5 inline h-3.5 w-3.5 text-brand" />
          </div>
        )}
        <div className="overflow-hidden rounded-lg border bg-card">
          {open.slice(0, 5).map((t, i) => (
            <Link
              key={t.id}
              to={`/portal/requests/${t.id}`}
              className={
                'flex items-center gap-3 px-4 py-3 hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring' +
                (i > 0 ? ' border-t' : '')
              }
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  {t.subject}{' '}
                  <span className="text-xs font-normal text-muted-foreground">
                    {ticketRef(t.type, t.number)}
                  </span>
                </span>
                <span className="block text-xs text-muted-foreground">
                  Updated {relativeTime(t.updatedAt)}
                  {t.assigneeName ? ` · Assigned to ${t.assigneeName}` : ''}
                </span>
              </span>
              <StatusChip status={t.status} category={t.statusCategory} />
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
